import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../api/api";

export default function MyTournamentFighters() {
  const { tournamentId } = useParams();

  const [fighters, setFighters] = useState([]);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unregistering, setUnregistering] = useState(null);

  // =========================================================
  // IMAGE
  // =========================================================

  const resolveImage = (path, type = "fighter") => {
    if (!path) return "";

    if (String(path).startsWith("http")) {
      return path;
    }

    let clean = String(path).replace(/^\/+/, "");

    if (clean.startsWith("uploads/")) {
      clean = clean.replace(/^uploads\//, "");
    }

    if (type === "club" && !clean.startsWith("clubs/")) {
      clean = `clubs/${clean}`;
    }

    return `https://warfc.com/api/uploads/${clean}`;
  };

  // =========================================================
  // LOAD MY FIGHTERS
  // =========================================================

  async function loadFighters() {
  let timeoutId;

  try {
    setLoading(true);
    setError("");

    // Never allow the page to hang forever.
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(
          new Error(
            "The server took too long to respond. Please try again."
          )
        );
      }, 15000);
    });

    const requestPromise = API.get(
      `/tournaments/${tournamentId}/my-fighters`
    );

    const result = await Promise.race([
      requestPromise,
      timeoutPromise,
    ]);

    setClub(result?.club || null);

    setFighters(
      Array.isArray(result?.fighters)
        ? result.fighters
        : []
    );

  } catch (err) {
    console.error(
      "Failed to load my tournament fighters:",
      err
    );

    setError(
      err?.message ||
        "Failed to load your tournament fighters."
    );

    setFighters([]);
    setClub(null);

  } finally {
    clearTimeout(timeoutId);
    setLoading(false);
  }
}

  useEffect(() => {
    if (tournamentId) {
      loadFighters();
    }
  }, [tournamentId]);

  // =========================================================
  // SUMMARY
  // =========================================================

  const summary = useMemo(() => {
    return {
      total: fighters.length,

      paid: fighters.filter(
        (f) => f.paid
      ).length,

      unpaid: fighters.filter(
        (f) => !f.paid
      ).length,

      approved: fighters.filter(
        (f) => f.approved
      ).length,

      pending: fighters.filter(
        (f) => !f.approved
      ).length,

      weighedIn: fighters.filter(
        (f) => f.weighed_in
      ).length,
    };
  }, [fighters]);

  // =========================================================
  // UNREGISTER
  // =========================================================

  async function handleUnregister(fighter) {
    if (!fighter?.registration_id) {
      return;
    }

    const confirmed = window.confirm(
      `Unregister ${fighter.name} ${fighter.surname} from this tournament?\n\n` +
        `This registration will be cancelled.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setUnregistering(
        fighter.registration_id
      );
      setError("");

      await API.delete(
        `/tournaments/${tournamentId}/registration/${fighter.registration_id}`
      );

      setFighters((current) =>
        current.filter(
          (f) =>
            f.registration_id !==
            fighter.registration_id
        )
      );
    } catch (err) {
      console.error(
        "Failed to unregister fighter:",
        err
      );

      setError(
        err?.message ||
          "Failed to unregister fighter."
      );
    } finally {
      setUnregistering(null);
    }
  }

  // =========================================================
  // STATUS
  // =========================================================

  function StatusBadge({
    value,
    yes = "YES",
    no = "PENDING",
  }) {
    return value ? (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold">
        ✓ {yes}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold">
        {no}
      </span>
    );
  }

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0d12] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-gray-700 border-t-yellow-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">
            Loading your fighters...
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // ERROR
  // =========================================================

  if (error && !fighters.length) {
    return (
      <div className="min-h-screen bg-[#0b0d12] text-white">
        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
            <h1 className="text-xl font-bold text-red-400 mb-2">
              Unable to load fighters
            </h1>

            <p className="text-gray-400">
              {error}
            </p>

            <button
              onClick={loadFighters}
              className="mt-5 px-5 py-2.5 rounded-xl bg-yellow-400 text-black font-bold hover:bg-yellow-300"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <div className="min-h-screen bg-[#0b0d12] text-gray-200">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-8">

          <div>
            <Link
              to={`/tournaments/${tournamentId}`}
              className="text-sm text-gray-500 hover:text-yellow-400 transition"
            >
              ← Back to Tournament
            </Link>

            <div className="flex items-center gap-4 mt-4">

              {club?.club_logo && (
                <img
                  src={resolveImage(
                    club.club_logo,
                    "club"
                  )}
                  alt={club.club_name}
                  className="w-14 h-14 rounded-xl object-contain bg-white/5 border border-gray-800 p-1"
                />
              )}

              <div>
                <p className="text-xs uppercase tracking-widest text-yellow-400 font-bold">
                  Club Coach
                </p>

                <h1 className="text-3xl md:text-4xl font-black text-white">
                  My Fighters
                </h1>

                <p className="text-gray-500 mt-1">
                  {club?.club_name || "Your Club"}
                </p>
              </div>

            </div>
          </div>

          <button
            onClick={loadFighters}
            className="px-4 py-2.5 rounded-xl bg-[#151923] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-700 transition"
          >
            ↻ Refresh
          </button>

        </div>

        {/* =================================================
            SUMMARY
        ================================================= */}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">

          <div className="bg-[#151923] border border-gray-800 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Fighters
            </p>

            <p className="text-3xl font-black text-white mt-1">
              {summary.total}
            </p>
          </div>

          <div className="bg-[#151923] border border-emerald-500/10 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Paid
            </p>

            <p className="text-3xl font-black text-emerald-400 mt-1">
              {summary.paid}
            </p>
          </div>

          <div className="bg-[#151923] border border-amber-500/10 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Unpaid
            </p>

            <p className="text-3xl font-black text-amber-400 mt-1">
              {summary.unpaid}
            </p>
          </div>

          <div className="bg-[#151923] border border-cyan-500/10 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Approved
            </p>

            <p className="text-3xl font-black text-cyan-400 mt-1">
              {summary.approved}
            </p>
          </div>

          <div className="bg-[#151923] border border-purple-500/10 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Weighed In
            </p>

            <p className="text-3xl font-black text-purple-400 mt-1">
              {summary.weighedIn}
            </p>
          </div>

        </div>

        {/* =================================================
            ERROR AFTER SUCCESSFUL LOAD
        ================================================= */}

        {error && fighters.length > 0 && (
          <div className="mb-5 bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400">
            {error}
          </div>
        )}

        {/* =================================================
            EMPTY
        ================================================= */}

        {!fighters.length ? (
          <div className="bg-[#151923] border border-gray-800 rounded-2xl p-12 text-center">

            <div className="text-5xl mb-4">
              🥊
            </div>

            <h2 className="text-xl font-bold text-white">
              No fighters registered yet
            </h2>

            <p className="text-gray-500 mt-2">
              Register your club fighters for this tournament.
            </p>

            <Link
              to={`/tournaments/${tournamentId}/register`}
              className="inline-flex mt-6 px-6 py-3 rounded-xl bg-yellow-400 text-black font-black hover:bg-yellow-300"
            >
              Register a Fighter
            </Link>

          </div>
        ) : (

          /* =================================================
             FIGHTERS
          ================================================= */

          <div className="space-y-4">

            {fighters.map((fighter) => (

              <div
                key={fighter.registration_id}
                className="bg-[#151923] border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition"
              >

                <div className="p-4 md:p-5">

                  <div className="flex flex-col lg:flex-row lg:items-center gap-5">

                    {/* FIGHTER */}

                    <div className="flex items-center gap-4 min-w-0 lg:w-[280px]">

                      {fighter.fighter_image ? (
                        <img
                          src={resolveImage(
                            fighter.fighter_image
                          )}
                          alt={fighter.name}
                          className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover bg-gray-900 border border-gray-800"
                        />
                      ) : (
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gray-900 border border-gray-800 flex items-center justify-center text-2xl">
                          🥊
                        </div>
                      )}

                      <div className="min-w-0">

                        <h2 className="text-lg md:text-xl font-black text-white truncate">
                          {fighter.name}{" "}
                          {fighter.surname}
                        </h2>

                        {fighter.nickname && (
                          <p className="text-sm text-gray-500 truncate">
                            "{fighter.nickname}"
                          </p>
                        )}

                        <p className="text-xs text-yellow-400 font-bold mt-1">
                          {fighter.fighter_gender ||
                            fighter.registration_gender ||
                            "—"}
                        </p>

                      </div>

                    </div>

                    {/* DIVISION */}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">

                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-600">
                          Age Division
                        </p>

                        <p className="text-sm font-bold text-white mt-1">
                          {fighter.age_division ||
                            "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-600">
                          Weight
                        </p>

                        <p className="text-sm font-bold text-white mt-1">
                          {fighter.weightclass_name ||
                            "—"}
                        </p>

                        {fighter.weight != null && (
                          <p className="text-xs text-gray-500">
                            {fighter.weight} kg
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-600">
                          Category
                        </p>

                        <p className="text-sm font-bold text-white mt-1">
                          {fighter.type ===
                          "altan_garaa"
                            ? "Altan Garaa"
                            : "Үндсэн"}
                        </p>
                      </div>

                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-600">
                          Registration
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          #{fighter.registration_id}
                        </p>
                      </div>

                    </div>

                    {/* STATUS */}

                    <div className="flex flex-wrap lg:flex-col gap-2 lg:min-w-[130px]">

                      <StatusBadge
                        value={fighter.paid}
                        yes="PAID"
                        no="UNPAID"
                      />

                      <StatusBadge
                        value={fighter.approved}
                        yes="APPROVED"
                        no="PENDING"
                      />

                      <StatusBadge
                        value={fighter.weighed_in}
                        yes="WEIGHED IN"
                        no="NOT WEIGHED"
                      />

                    </div>

                    {/* ACTIONS */}

                    <div className="flex lg:flex-col gap-2 lg:w-[130px]">

                      <Link
                        to={`/tournaments/${tournamentId}/register?fighter=${fighter.fighter_id}`}
                        className="flex-1 lg:w-full text-center px-4 py-2.5 rounded-xl bg-[#202532] border border-gray-700 text-gray-200 text-sm font-bold hover:bg-[#293040] transition"
                      >
                        Manage
                      </Link>

                      <button
                        onClick={() =>
                          handleUnregister(
                            fighter
                          )
                        }
                        disabled={
                          unregistering ===
                          fighter.registration_id
                        }
                        className="flex-1 lg:w-full px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold hover:bg-red-500/20 disabled:opacity-50 transition"
                      >
                        {unregistering ===
                        fighter.registration_id
                          ? "Removing..."
                          : "Unregister"}
                      </button>

                    </div>

                  </div>

                </div>

              </div>

            ))}

          </div>
        )}

      </div>
    </div>
  );
}
